import React, { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { RefreshCw, RotateCcw } from 'lucide-react'
import { request } from '@/lib/request'
import { toast } from 'sonner'

export interface ManageField {
  desc: string
  field: string
  NoShowInTable?: boolean
  render?: (value: any, row: any, index: number) => React.ReactNode
  Comp?: React.ReactNode
  initialValue?: any
  valuePropName?: string
}

export interface ManageTableProps {
  fields: ManageField[]
  collect: string
  filters?: Record<string, any>
  filter?: React.ReactNode
  modalWidth?: string | number
  onResetFilters?: () => void
  customActions?: (row: any) => React.ReactNode
  hideAddButton?: boolean
  customAddButton?: React.ReactNode
  primaryKey?: string
}

export function ManageTable({
  fields,
  collect,
  filters: propFilters,
  filter,
  modalWidth = '600px',
  onResetFilters,
  customActions,
  hideAddButton = false,
  customAddButton,
  primaryKey = 'id',
}: ManageTableProps) {
  const [list, setList] = useState<any[]>([])
  const [isShow, setIsShow] = useState(false)
  const [current, setCurrent] = useState<any>(null)
  const [formData, setFormData] = useState<Record<string, any>>({})

  const [pageSize, setPageSize] = useState(10)
  const [pageNum, setPageNum] = useState(1)
  const [total, setTotal] = useState(0)
  const latestRequestRef = useRef(0)
  const latestAppliedRequestRef = useRef(0)
  const filterKey = JSON.stringify(propFilters || {})

  const [deleteConfirm, setDeleteConfirm] = useState<{
    open: boolean
    row: any
  }>({ open: false, row: null })

  useEffect(() => {
    if (pageNum !== 1) {
      setPageNum(1)
    }
  }, [filterKey])

  const getData = async () => {
    const requestId = ++latestRequestRef.current
    try {
      const resp = await request(`/system/api/query/${collect}`, {
        page_size: pageSize,
        page_num: pageNum,
        query_filter: propFilters || {},
      })
      if (
        requestId > latestAppliedRequestRef.current &&
        resp &&
        resp.code === 0
      ) {
        latestAppliedRequestRef.current = requestId
        setList(
          resp.data.list.map((val: any) => {
            val.key = val[primaryKey]
            return val
          })
        )
        setTotal(resp.data.total)
      }
    } catch (error) {
      console.error('Failed to fetch data:', error)
    }
  }

  useEffect(() => {
    getData()
  }, [pageSize, pageNum, filterKey])

  useEffect(() => {
    if (!isShow) {
      setCurrent(null)
      setFormData({})
    } else {
      // Initialize form data
      const initialData: Record<string, any> = {}
      fields.forEach((field) => {
        if (current && current[field.field] !== undefined) {
          initialData[field.field] = current[field.field]
        } else if (field.initialValue !== undefined) {
          initialData[field.field] = field.initialValue
        }
      })
      setFormData(initialData)
    }
  }, [isShow, current])

  const handleSubmit = async () => {
    const submitData = { ...formData }
    if (current) {
      submitData[primaryKey] = current[primaryKey]
    }

    try {
      const resp = await request(
        `/system/api/${current ? 'update' : 'add'}/${collect}`,
        submitData
      )
      if (resp && resp.code === 0) {
        toast.success(`${current ? '更新' : '添加'}成功`)
        getData()
        setIsShow(false)
      } else {
        toast.error(`${current ? '更新' : '添加'}失败`)
      }
    } catch (error) {
      toast.error(`${current ? '更新' : '添加'}失败`)
    }
  }

  const handleDelete = async () => {
    const row = deleteConfirm.row
    try {
      const resp = await request(`/system/api/delete/${collect}`, {
        [primaryKey]: row[primaryKey],
      })
      if (resp && resp.code === 0) {
        toast.success('删除成功')
        getData()
      } else {
        toast.error('删除失败')
      }
    } catch (error) {
      toast.error('删除失败')
    }
    setDeleteConfirm({ open: false, row: null })
  }

  const getColumns = () => {
    const cols = fields
      .filter((val) => !val.NoShowInTable)
      .map((val) => ({
        title: val.desc,
        dataIndex: val.field,
        key: val.field,
        render: val.render,
      }))

    cols.push({
      title: '操作',
      dataIndex: 'actions',
      key: 'actions',
      render: undefined,
    })

    return cols
  }

  const columns = getColumns()

  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }))
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        {!hideAddButton && !customAddButton && (
          <Button onClick={() => setIsShow(true)}>添加</Button>
        )}
        {customAddButton && <div>{customAddButton}</div>}
        {filter && <div>{filter}</div>}
        <div className="flex gap-2">
          {onResetFilters && (
            <Button variant="outline" onClick={onResetFilters}>
              <RotateCcw className="h-4 w-4 mr-2" />
              重置
            </Button>
          )}
          <Button variant="outline" onClick={() => getData()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            刷新
          </Button>
        </div>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((col) => (
                <TableHead key={col.key}>{col.title}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="text-center text-muted-foreground">
                  暂无数据
                </TableCell>
              </TableRow>
            ) : (
              list.map((row, rowIndex) => (
                <TableRow key={row.key || rowIndex}>
                  {columns.map((col, colIndex) => (
                    <TableCell key={col.key}>
                      {col.key === 'actions' ? (
                        <div className="flex gap-2">
                          {customActions && customActions(row)}
                          <Button
                            size="sm"
                            onClick={() => {
                              setCurrent(row)
                              setIsShow(true)
                            }}
                          >
                            更新
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => {
                              setDeleteConfirm({ open: true, row })
                            }}
                          >
                            删除
                          </Button>
                        </div>
                      ) : col.render ? (
                        col.render(row[col.dataIndex], row, rowIndex)
                      ) : (
                        row[col.dataIndex]
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          共 {total} 条记录
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={pageNum === 1}
            onClick={() => setPageNum((p) => Math.max(1, p - 1))}
          >
            上一页
          </Button>
          <span className="text-sm">
            第 {pageNum} 页 / 共 {Math.ceil(total / pageSize)} 页
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={pageNum >= Math.ceil(total / pageSize)}
            onClick={() => setPageNum((p) => p + 1)}
          >
            下一页
          </Button>
          <select
            className="border rounded px-2 py-1 text-sm"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value))
              setPageNum(1)
            }}
          >
            <option value={10}>10 条/页</option>
            <option value={20}>20 条/页</option>
            <option value={50}>50 条/页</option>
            <option value={100}>100 条/页</option>
          </select>
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isShow} onOpenChange={setIsShow}>
        <DialogContent style={{ maxWidth: modalWidth }} className="max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{current ? '更新' : '添加'}</DialogTitle>
            <DialogDescription>
              {current ? '更新现有记录' : '添加新记录'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 overflow-y-auto max-h-[60vh]">
            {fields.map((item, index) => (
              <div key={index} className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor={item.field} className="text-right">
                  {item.desc}
                </Label>
                <div className="col-span-3">
                  {item.Comp ? (
                    React.cloneElement(item.Comp as React.ReactElement<any>, {
                      [item.valuePropName || 'value']: formData[item.field],
                      // Handle both onChange and onCheckedChange for Switch components
                      ...(item.valuePropName === 'checked'
                        ? {
                            onCheckedChange: (checked: boolean) => {
                              handleFieldChange(item.field, checked)
                            },
                          }
                        : {
                            onChange: (valueOrEvent: any) => {
                              // Handle different types of onChange
                              let value
                              if (valueOrEvent?.target?.value !== undefined) {
                                // Standard input event
                                value = valueOrEvent.target.value
                              } else if (valueOrEvent?.target?.checked !== undefined) {
                                // Checkbox event
                                value = valueOrEvent.target.checked
                              } else {
                                // Direct value (Select, InputNumber, CodeEditor, etc.)
                                value = valueOrEvent
                              }
                              handleFieldChange(item.field, value)
                            },
                          }),
                    })
                  ) : (
                    <Input
                      id={item.field}
                      value={formData[item.field] || ''}
                      onChange={(e) =>
                        handleFieldChange(item.field, e.target.value)
                      }
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsShow(false)}>
              取消
            </Button>
            <Button onClick={handleSubmit}>确定</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirm.open} onOpenChange={(open) => setDeleteConfirm({ open, row: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除</AlertDialogTitle>
            <AlertDialogDescription>
              此操作无法撤销。确定要删除这条记录吗?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>确定</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
